import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, MessageSquare, Bell, Users, Calendar, Share2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

const CommunicationFeature = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: MessageSquare,
      title: 'Team Chat & Messaging',
      description: 'Real-time group chats, direct messages, and announcement channels keep your team connected and informed instantly.'
    },
    {
      icon: Bell,
      title: 'Smart Notifications',
      description: 'Push notifications for schedule changes, shift reminders, approvals, and urgent announcements with customizable preferences.'
    },
    {
      icon: Calendar,
      title: 'Newsfeed & Updates',
      description: 'Centralized feed of company announcements, policy changes, celebrations, and important updates everyone needs to see.'
    },
    {
      icon: Share2,
      title: 'File Sharing',
      description: 'Share documents, images, training materials, and important files directly within conversations for easy access.'
    },
    {
      icon: Users,
      title: 'Department Channels',
      description: 'Organized channels by department, location, or project keep conversations relevant and searchable.'
    },
    {
      icon: CheckCircle2,
      title: 'Read Receipts',
      description: 'Know exactly who has seen critical messages and announcements with delivery and read confirmation tracking.'
    }
  ];

  const benefits = [
    'Reduce email clutter by 90% with centralized messaging',
    'Instant reach to entire workforce or specific groups',
    'Searchable message history for reference and compliance',
    'Mobile app keeps remote workers connected',
    '@ mentions for direct attention and notifications',
    'Rich media support for images, videos, and documents',
    'Emergency broadcast for critical announcements',
    'Integration with calendars and scheduling',
    'Translation support for multilingual teams',
    'Message pinning for important information'
  ];

  const useCases = [
    {
      title: 'Shift Change Notifications',
      description: 'Automatically notify affected employees when schedules change. Employees can acknowledge receipt and managers can see who has been informed. No more phone tag or missed communications.'
    },
    {
      title: 'Team ColEmployeeation',
      description: 'Create project-specific channels where team members can coordinate work, share updates, ask questions, and resolve issues in real-time. Keep everything organized and searchable.'
    },
    {
      title: 'Company Announcements',
      description: 'Broadcast important company news, policy updates, safety alerts, and celebrations to everyone instantly. Track readership and ensure critical information reaches all employees.'
    },
    {
      title: 'Remote Team Connection',
      description: 'Keep distributed teams feeling connected with casual conversation channels, virtual watercooler chats, and social channels that build culture and camaraderie remotely.'
    }
  ];

  return (
    <>
      <LandingNav />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Team Communication Hub
            </h1>
            <p className="text-xl md:text-2xl text-cyan-100 mb-8 max-w-4xl mx-auto">
              Keep your entire team connected, informed, and engaged. Replace scattered emails and texts with one powerful communication platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/signup')}
                className="bg-white text-cyan-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all flex items-center justify-center"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
              <button 
                onClick={() => navigate('/contact')}
                className="bg-cyan-500/20 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-cyan-500/30 transition-all"
              >
                Schedule Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Screenshot Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-200">
            <img 
              src="/Newsfeed.png" 
              alt="Team Communication Hub Interface"
              className="w-full h-auto rounded-xl"
            />
          </div>
          <div className="grid grid-cols-3 gap-6 mt-8">
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-cyan-600 mb-2">90%</div>
              <div className="text-gray-600">Less Email</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">Instant</div>
              <div className="text-gray-600">Team Reach</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">100%</div>
              <div className="text-gray-600">Engagement</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Everything Your Team Needs to Stay Connected
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            From instant messaging to company-wide announcements, all your communication in one place.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border border-cyan-100 hover:shadow-xl transition-all"
                >
                  <div className="w-14 h-14 bg-cyan-600 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Transform Team Communication
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Stop chasing people through multiple apps. Centralize all team communication in one platform everyone actually uses.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">How It Works</h3>
              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Create Channels</h4>
                    <p className="text-gray-600">Organize by department, location, project, or topic. Public or private as needed.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Invite Team Members</h4>
                    <p className="text-gray-600">Add employees to relevant channels. They receive instant access on mobile and web.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Start Communicating</h4>
                    <p className="text-gray-600">Send messages, share files, make announcements. Everyone stays informed in real-time.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Track Engagement</h4>
                    <p className="text-gray-600">See who read important messages. Search history. Keep everyone accountable.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Communication for Every Scenario
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            From routine updates to emergency alerts, stay connected with your team always.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-50 to-cyan-50 rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-all"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{useCase.title}</h3>
                <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Bring Your Team Together
          </h2>
          <p className="text-xl text-cyan-100 mb-8">
            Join thousands of teams communicating better with our integrated messaging platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="bg-white text-cyan-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all"
            >
              Start Free 14-Day Trial
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all"
            >
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default CommunicationFeature;
